import axios, {AxiosResponse} from 'axios'
import {TenantSettingsFormValues, TenantSettingsResponse} from './_models'
import {
  createDefaultManualExamLayoutSettings,
  normalizeManualExamLayoutSettings,
} from '../../../my-course-exam/manual/core/_models'

const API_URL = process.env.REACT_APP_API_URL
const CUSTOM_URL = `${API_URL}/Custom`
const GET_SETTINGS_URL = `${CUSTOM_URL}/GetTenantSettings`
const SAVE_SETTINGS_URL = `${CUSTOM_URL}/SaveTenantSettings`
const DOWNLOAD_LOGO_URL = `${CUSTOM_URL}/DownloadInstitutionLogo`
const RESET_CACHE_URL = `${CUSTOM_URL}/resetCache`

const DEFAULT_RESPONSE: Pick<
  TenantSettingsResponse,
  | 'smsLoginEnabled'
  | 'hasInstitutionLogo'
  | 'measurementCenterActive'
  | 'fixedFiveOptionMultipleChoice'
  | 'manualExamFixedBookletCount'
  | 'manualExamDynamicLayoutEnabled'
  | 'manualExamDefaultLayout'
  | 'isObsActive'
> = {
  smsLoginEnabled: false,
  hasInstitutionLogo: false,
  measurementCenterActive: false,
  fixedFiveOptionMultipleChoice: false,
  manualExamFixedBookletCount: null,
  manualExamDynamicLayoutEnabled: false,
  manualExamDefaultLayout: createDefaultManualExamLayoutSettings(),
  isObsActive: false,
}

export const fetchTenantSettings = async (): Promise<TenantSettingsResponse> => {
  const response: AxiosResponse<TenantSettingsResponse> = await axios.get(GET_SETTINGS_URL)
  const data = response.data
  let manualExamFixedBookletCount: number | null = null
  const rawFixedCount = (data as any)?.manualExamFixedBookletCount
  if (typeof rawFixedCount === 'number') {
    manualExamFixedBookletCount = Number.isFinite(rawFixedCount) ? rawFixedCount : null
  } else if (typeof rawFixedCount === 'string' && rawFixedCount.trim().length > 0) {
    const parsed = Number(rawFixedCount)
    manualExamFixedBookletCount = Number.isFinite(parsed) ? parsed : null
  }
  if (manualExamFixedBookletCount !== null) {
    if (manualExamFixedBookletCount < 1) {
      manualExamFixedBookletCount = 1
    }
    if (manualExamFixedBookletCount > 4) {
      manualExamFixedBookletCount = 4
    }
  }
  return {
    ...DEFAULT_RESPONSE,
    ...data,
    manualExamFixedBookletCount,
    manualExamDefaultLayout: normalizeManualExamLayoutSettings(
      (data as any)?.manualExamDefaultLayout
    ),
  }
}

export const saveTenantSettings = async (
  values: TenantSettingsFormValues,
  logoFile?: File | null
): Promise<TenantSettingsResponse> => {
  const formData = new FormData()

  if (values.id !== undefined && values.id !== null) {
    formData.append('Id', String(values.id))
  }

  formData.append('Name', values.name ?? '')
  formData.append('SmsLoginEnabled', values.smsLoginEnabled ? 'true' : 'false')
  formData.append('MeasurementCenterActive', values.measurementCenterActive ? 'true' : 'false')
  formData.append(
    'FixedFiveOptionMultipleChoice',
    values.fixedFiveOptionMultipleChoice ? 'true' : 'false'
  )
  formData.append(
    'ManualExamFixedBookletCount',
    values.manualExamFixedBookletCount !== undefined && values.manualExamFixedBookletCount !== null
      ? String(values.manualExamFixedBookletCount)
      : ''
  )
  formData.append(
    'ManualExamDynamicLayoutEnabled',
    values.manualExamDynamicLayoutEnabled ? 'true' : 'false'
  )
  if (values.manualExamDefaultLayout) {
    formData.append(
      'ManualExamDefaultLayoutJson',
      JSON.stringify(normalizeManualExamLayoutSettings(values.manualExamDefaultLayout))
    )
  } else {
    formData.append('ManualExamDefaultLayoutJson', '')
  }
  formData.append('InstitutionDisplayName', values.institutionDisplayName ?? '')
  formData.append('GlobalExamRules', values.globalExamRules ?? '')
  formData.append('ChatgptToken1', values.chatgptToken1 ?? '')
  formData.append('ChatgptToken2', values.chatgptToken2 ?? '')
  formData.append('ChatgptToken3', values.chatgptToken3 ?? '')
  formData.append('ChatgptToken4', values.chatgptToken4 ?? '')
  formData.append('ChatGptToken5', values.chatGptToken5 ?? '')
  formData.append('ChatgptToken6', values.chatgptToken6 ?? '')
  formData.append('RemoveInstitutionLogo', values.removeInstitutionLogo ? 'true' : 'false')
  formData.append('IsObsActive', values.isObsActive ? 'true' : 'false')

  if (values.number !== undefined && values.number !== null) {
    formData.append('Number', String(values.number))
  } else {
    formData.append('Number', '')
  }

  if (logoFile) {
    formData.append('InstitutionLogo', logoFile)
  }

  const response: AxiosResponse<{data: TenantSettingsResponse}> = await axios.post(
    SAVE_SETTINGS_URL,
    formData,
    {
      headers: {'Content-Type': 'multipart/form-data'},
    }
  )

  const payload = response.data?.data

  return {
    ...DEFAULT_RESPONSE,
    ...(payload ?? {}),
    manualExamDefaultLayout: normalizeManualExamLayoutSettings(
      (payload as any)?.manualExamDefaultLayout
    ),
  }
}

export const downloadInstitutionLogo = async (): Promise<Blob> => {
  const response = await axios.get(DOWNLOAD_LOGO_URL, {responseType: 'blob'})
  return response.data
}

export const resetApplicationCache = async (): Promise<void> => {
  await axios.get(RESET_CACHE_URL)
}
